import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  HStack,
  LinkBox,
  Stack,
  StackProps,
  Text,
  TextProps,
} from '@cardboard-ui/react';
import React, { useRef } from 'react';
import { graphql } from 'relay-runtime';
import { useFragment } from 'utils/graphClient';
import { UserAvatar, UserAvatarProps } from '../UserAvatar';
import { UserHandle_member$key } from './__generated__/UserHandle_member.graphql';
import { UserInfoPopover } from './components/UserInfoPopover';

const memberFragment = graphql`
  fragment UserHandle_member on Member {
    id
    name
    email
    ...UserAvatar_member
    ...UserInfoPopover_member
  }
`;

export type UserHandleProps = StackProps &
  Pick<TextProps, 'fontWeight' | 'as'> & {
    member: UserHandle_member$key;
    showAvatar?: boolean;
    avatarProps?: Omit<UserAvatarProps, 'member'>;
    avatarChildren?: React.ReactNode;
    nameProps?: StackProps;
    showEmail?: boolean;
    emailProps?: BoxProps;
    linkToProfile?: boolean;
    buttonProps?: ButtonProps;
    isSelected?: boolean;
  };

export const UserHandle: React.FC<UserHandleProps> = ({
  member,
  showAvatar,
  avatarProps,
  avatarChildren,
  nameProps,
  showEmail,
  emailProps,
  children,
  linkToProfile,
  spacing,
  buttonProps,
  fontWeight,
  as,
  ...otherProps
}) => {
  const _member = useFragment(memberFragment, member);
  const containerRef = useRef(null);

  return (
    <LinkBox {...otherProps} ref={containerRef}>
      <HStack spacing={spacing || 2}>
        {showAvatar && (
          <UserAvatar member={_member} {...avatarProps}>
            {avatarChildren}
          </UserAvatar>
        )}

        <Stack overflow="hidden" spacing={0}>
          <HStack maxWidth="xl" {...nameProps}>
            <Text as={as || 'h4'} fontWeight={fontWeight || 500} noOfLines={1}>
              {linkToProfile ? (
                <UserInfoPopover member={_member} {...avatarProps}>
                  <Button
                    variant="link"
                    colorScheme="black.alpha"
                    minW={0}
                    whiteSpace="normal"
                    textAlign="left"
                    {...buttonProps}
                  >
                    <Text noOfLines={1}>{_member.name}</Text>
                  </Button>
                </UserInfoPopover>
              ) : (
                _member.name
              )}
            </Text>{' '}
            {children}
          </HStack>

          {showEmail && (
            <Box fontSize="sm" overflow="hidden" minWidth={0} {...emailProps}>
              <SplitEmail email={_member.email} />
            </Box>
          )}
        </Stack>
      </HStack>
    </LinkBox>
  );
};

const SplitEmail = ({ email }: { email: string }) => {
  const [account, domain] = email.split('@');
  return (
    <>
      <div style={{ display: 'inline-block' }}>{account}</div>
      <div style={{ display: 'inline-block' }}>@{domain}</div>
    </>
  );
};
